import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import { partition, reduce } from 'lodash';
import * as React from 'react';

import {
    EXTERNAL_REASON_KEY,
    OTHER_REASON_KEY,
    OUTDOOR_ID_KEY
} from '../../mappers/moderation';
import { Bem } from '../../service/Bem/Bem';
import { getModerationRejectReasonTitle } from '../../service/Moderation/Moderation';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BasePopupProps, Lang, LinkTarget, Tld } from '../../typings/common';
import { AdvType } from '../../typings/domain';
import {
    ModerationRejectKey,
    ModerationRejectOutdoorReasonsItem,
    ModerationRejectReason,
    OutdoorRejectFullReason
} from '../../typings/moderation';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { Collapser } from '../Collapser/Collapser';
import { Loader } from '../Loader/Loader';
import { ModerationRejectReasonDescription } from '../ModerationRejectReasonDescription/ModerationRejectReasonDescription';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { Shape2, Shape2Icon, Shape2Tone } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './ModerationRejectReasons.css';
import * as keyset from './ModerationRejectReasons.i18n';

const i18n = i18nFactory(keyset);

const i18nRaw = i18nRawFactory(keyset);

const BLOCK = 'moderation-reject-reasons';
const MODERATION_REASONS_KEYS = [
    OTHER_REASON_KEY,
    EXTERNAL_REASON_KEY,
    OUTDOOR_ID_KEY
];

export interface ModerationRejectReasonsProps {
    cls?: string;
    reasons?: Array<ModerationRejectReason>;
    contactEmail?: string;
    bid?: string;
    bannerType?: AdvType;
    isOperatorSelfAgency: boolean;
    isOperatorAgency: boolean;
    clientRegion: string;
    lang: Lang;
    tld: Tld;
    error?: string;
    button?: JSX.Element;
}

enum FooterLink {
    reasonsQuestion = 'reasonsQuestion',
    generalAdvRules = 'generalAdvRules',
    sendDocs = 'sendDocs',
    paymentReturn = 'paymentReturn',
    serviceQuestion = 'serviceQuestion',
    checkAgain = 'checkAgain',
    falseInfoProofsText = 'falseInfoProofsText'
}

export class ModerationRejectReasons extends React.PureComponent<
    ModerationRejectReasonsProps & BasePopupProps
> {
    private popupSwitcher: React.ReactNode | null;

    render() {
        const { cls, switchPopupState, isPopupVisible } = this.props;

        return (
            <div className={classNames(cls, Bem.cls(BLOCK))}>
                <StyledLink
                    cls={Bem.cls(BLOCK, 'switcher', {
                        active: isPopupVisible
                    })}
                    theme={StyledLinkTheme.grey}
                    onClick={switchPopupState}
                    ref={ref => (this.popupSwitcher = ref)}
                >
                    {this.getButton()}
                </StyledLink>

                {this.getPopup()}
            </div>
        );
    }

    private getButton = () => {
        const { button } = this.props;

        if (button) {
            return button;
        }

        return (
            <Shape2
                cls={Bem.cls(BLOCK, 'switcher-icon')}
                width={12}
                height={12}
                icon={Shape2Icon.info}
                tone={Shape2Tone.dark}
            />
        );
    };

    private getPopup = () => {
        const { isPopupVisible, closePopup } = this.props;

        return (
            <PopupDecorator
                cls={Bem.cls(BLOCK, 'popup')}
                maxBodyHeight={300}
                onOutsideClick={closePopup}
                target={'anchor'}
                anchor={this.popupSwitcher}
                visible={isPopupVisible}
                directions={[
                    'right-center',
                    'top-left',
                    'bottom-left',
                    'left-center'
                ]}
                hasTail={true}
                header={i18n('popup-title')}
                footer={this.getFooter()}
                hasClose={true}
                onClose={closePopup}
            >
                {this.getContent()}
            </PopupDecorator>
        );
    };

    private getContent = () => {
        const { reasons, error } = this.props;

        if (error) {
            return this.getErrorContent();
        }

        if (reasons === undefined) {
            return this.getLoadingContent();
        }

        if (reasons.length === 0) {
            return this.getGeneralReasonContent();
        }

        // todo такие сложные проверки из-за неправильной структуры данных
        //  будет исправлено в DIRECT-93277 author: mrronuar
        if (reasons.length === 1) {
            const outdoorReasons = reasons[0];
            if (outdoorReasons.key === OUTDOOR_ID_KEY) {
                return this.getOutdoorContent(outdoorReasons.reasons);
            }

            return this.getSingleReasonContent();
        }

        const [groupedReasons, flatReasons] = partition(
            reasons,
            reason => reason.key === OUTDOOR_ID_KEY
        );

        const hasOutdoorReasons = groupedReasons.length === 1;

        return (
            <>
                <Collapser
                    items={flatReasons.map(
                        (reason: ModerationRejectReason, i) => {
                            if (reason.key === EXTERNAL_REASON_KEY) {
                                return {
                                    key: `externalReason${i}`,
                                    title: reason.title,
                                    content: this.getExternalContent(
                                        reason.text
                                    )
                                };
                            }
                            const reasonData = this.getReasonData(reason);
                            return {
                                key: reason.key,
                                title: reasonData.title,
                                content: reasonData.content
                            };
                        },
                        this
                    )}
                />
                {hasOutdoorReasons &&
                    this.getOutdoorContent(
                        (groupedReasons[0] as OutdoorRejectFullReason).reasons
                    )}
            </>
        );
    };

    private getLoadingContent = (): JSX.Element => {
        return (
            <div className={Bem.cls(BLOCK, 'loader')}>
                <Loader size={'l'} />
            </div>
        );
    };

    private getErrorContent = (): JSX.Element => {
        return (
            <div className={Bem.cls(BLOCK, 'error')}>{this.props.error}</div>
        );
    };

    private getOutdoorContent = (
        outdoorReasons: Array<ModerationRejectOutdoorReasonsItem>
    ) => {
        return (
            <ul className={Bem.cls(BLOCK, 'operators-list')}>
                {outdoorReasons.map(item => {
                    return (
                        <li
                            className={Bem.cls(BLOCK, 'operator-block')}
                            key={item.pageId}
                        >
                            <div className={Bem.cls(BLOCK, 'reason-title')}>
                                {item.pageName}
                            </div>
                            <Collapser
                                items={item.reasons.map(
                                    (reason: ModerationRejectReason, j) => {
                                        if (
                                            reason.key === EXTERNAL_REASON_KEY
                                        ) {
                                            return {
                                                key: `externalReason${j}`,
                                                title: reason.title,
                                                content: this.getExternalContent(
                                                    reason.text
                                                )
                                            };
                                        }
                                        const reasonData = this.getReasonData(
                                            reason
                                        );
                                        return {
                                            key: reason.key,
                                            title: reasonData.title,
                                            content: reasonData.content
                                        };
                                    },
                                    this
                                )}
                            />
                        </li>
                    );
                })}
            </ul>
        );
    };

    private getGeneralReasonContent = (): JSX.Element => {
        const { tld, lang } = this.props;
        const directAdvRulesLink = UrlHelpers.getLegalUrl(
                'direct_adv_rules',
                tld,
                lang
            ),
            advRulesLink = UrlHelpers.getLegalUrl('adv_rules', tld, lang);

        return (
            <div className={Bem.cls(BLOCK, 'reason-content')}>
                {i18nRaw('ubedites-chto-reklamnye-materialy', {
                    yandex: (
                        <StyledLink
                            theme={StyledLinkTheme.blue}
                            href={directAdvRulesLink}
                            target={LinkTarget.blank}
                        >
                            {i18n(
                                'ubedites-chto-reklamnye-materialy-param-yandex'
                            )}
                        </StyledLink>
                    ),
                    law: (
                        <StyledLink
                            theme={StyledLinkTheme.blue}
                            href={advRulesLink}
                            target={LinkTarget.blank}
                        >
                            {i18n(
                                'ubedites-chto-reklamnye-materialy-param-law'
                            )}
                        </StyledLink>
                    )
                })}
            </div>
        );
    };

    private getSingleReasonContent = (): JSX.Element | null => {
        const { reasons } = this.props;

        if (reasons === undefined) {
            return null;
        }

        const reasonData = this.getReasonData(reasons[0]);

        return (
            <>
                <h3 className={Bem.cls(BLOCK, 'reason-title')}>
                    {reasonData.title}
                </h3>

                <div className={Bem.cls(BLOCK, 'reason-content')}>
                    {reasonData.content}
                </div>
            </>
        );
    };

    private getExternalContent = (content: string) => {
        return (
            <div
                className={Bem.cls(BLOCK, 'external-content')}
                dangerouslySetInnerHTML={{
                    __html: content
                }}
            />
        );
    };

    private getReasonData = (
        reason: ModerationRejectReason
    ): { title: string; content: JSX.Element } => {
        if (reason.key !== EXTERNAL_REASON_KEY) {
            const {
                bid,
                bannerType,
                tld,
                lang,
                contactEmail,
                isOperatorAgency,
                clientRegion
            } = this.props;

            return {
                title: getModerationRejectReasonTitle(reason.key),
                content: (
                    <ModerationRejectReasonDescription
                        reason={reason}
                        bid={bid}
                        contactEmail={contactEmail}
                        bannerType={bannerType}
                        lang={lang}
                        tld={tld}
                        isOperatorAgency={isOperatorAgency}
                        clientRegion={clientRegion}
                    />
                )
            };
        }
        return {
            title: reason.title,
            content: this.getExternalContent(reason.text)
        };
    };

    private getFooter = (): Array<JSX.Element> | null => {
        const { error, reasons } = this.props;

        if (error || reasons === undefined) {
            return null;
        }

        const defaultLinks = [FooterLink.sendDocs, FooterLink.reasonsQuestion];
        const moderationRejectReasonData: Record<
            ModerationRejectKey,
            Array<FooterLink>
        > = {
            [ModerationRejectKey.kazakhLaw]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.copyright]: [
                FooterLink.sendDocs,
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.spam]: [
                FooterLink.paymentReturn,
                FooterLink.serviceQuestion
            ],
            [ModerationRejectKey.russianLaw]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.russianLawCallout]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.yandexPolicy]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.siteRequirements]: [FooterLink.checkAgain],
            [ModerationRejectKey.newsRequirements]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.falseInfo]: [FooterLink.falseInfoProofsText],
            [ModerationRejectKey.errors]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.tm]: [FooterLink.sendDocs],
            [ModerationRejectKey.invalidUrl]: [FooterLink.checkAgain],
            [ModerationRejectKey.politics]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.contactInfo]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.licenseRequired]: [
                FooterLink.sendDocs,
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.irrelevantKeywords]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.russianLawVcard]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.licenseRequiredVcard]: [
                FooterLink.sendDocs,
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.falseInfoVcard]: [
                FooterLink.falseInfoProofsText
            ],
            [ModerationRejectKey.phoneDoesntAnswer]: [FooterLink.checkAgain],
            [ModerationRejectKey.invalidContacts]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.yandexPolicyAuto]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.yandexPolicyVcard]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.blacklist]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.errorsVcard]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.irrelevantImage]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.yandexPolicyImage]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.poorImage]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.russianLawImage]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.russianLawQl]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.qlRequirements]: [FooterLink.checkAgain],
            [ModerationRejectKey.errorsQl]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.invalidUrlQl]: [FooterLink.checkAgain],
            [ModerationRejectKey.urlToOtherDomain]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.licenseRequiredQl]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.irrelevantMobileContent]: [],
            [ModerationRejectKey.irrelevantSiteLinkDescription]: [],
            [ModerationRejectKey.yandexPolicyCallout]: [],
            [ModerationRejectKey.errorsCallout]: [],
            [ModerationRejectKey.contactInfoCallout]: [],
            [ModerationRejectKey.falseInfoCallout]: [],
            [ModerationRejectKey.dhrefRequirements]: [
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.licenseRequiredDhref]: [
                FooterLink.sendDocs,
                FooterLink.reasonsQuestion
            ],
            [ModerationRejectKey.tmDhref]: [FooterLink.sendDocs],
            [ModerationRejectKey.russianLawDhref]: [FooterLink.reasonsQuestion],
            [ModerationRejectKey.adImageBadText]: [],
            [ModerationRejectKey.adImageNoBorder]: [],
            [ModerationRejectKey.adImageBadQuality]: [],
            [ModerationRejectKey.adImageNoSource]: [],
            [ModerationRejectKey.adImageNasty]: [],
            [ModerationRejectKey.adImageInterfaceElement]: [],
            [ModerationRejectKey.adImageFinance]: [],
            [ModerationRejectKey.adImageLottery]: [],
            [ModerationRejectKey.adImageShareBuilding]: [],
            [ModerationRejectKey.adImageDistance]: [],
            [ModerationRejectKey.adImageMedicine]: [],
            [ModerationRejectKey.adImageSmi]: [],
            [ModerationRejectKey.adImageBabyfood]: [],
            [ModerationRejectKey.adImageHouseCooperative]: [],
            [ModerationRejectKey.videoSmi]: [],
            [ModerationRejectKey.videoMedicine]: [],
            [ModerationRejectKey.videoHouseCooperative]: [],
            [ModerationRejectKey.videoLottery]: [],
            [ModerationRejectKey.videoShareBuilding]: [],
            [ModerationRejectKey.videoFinance]: [],
            [ModerationRejectKey.videoDistanceSales]: [],
            [ModerationRejectKey.videoBabyfood]: [],
            [ModerationRejectKey.videoRequirements]: [],
            [ModerationRejectKey.audioRequirements]: [],
            [ModerationRejectKey.turbolandingRequirements]: [
                FooterLink.generalAdvRules,
                FooterLink.checkAgain
            ],
            [ModerationRejectKey.turbolandingRequirementsSlink]: [
                FooterLink.generalAdvRules,
                FooterLink.checkAgain
            ],
            [ModerationRejectKey.html5Nasty]: [],
            [ModerationRejectKey.html5Babyfood]: [],
            [ModerationRejectKey.html5Finance]: [],
            [ModerationRejectKey.html5Funds]: [],
            [ModerationRejectKey.html5BadQuality]: [],
            [ModerationRejectKey.html5RussianLaw]: [],
            [ModerationRejectKey.html5Illness]: [],
            [ModerationRejectKey.html5Spam]: [],
            [ModerationRejectKey.html5HouseCooperative]: [],
            [ModerationRejectKey.html5InterfaceElement]: [],
            [ModerationRejectKey.html5NoBorder]: [],
            [ModerationRejectKey.html5HrefsErrors]: [],
            [ModerationRejectKey.html5NoSource]: [],
            [ModerationRejectKey.html5Smi]: [],
            [ModerationRejectKey.html5Securities]: [],
            [ModerationRejectKey.html5Lottery]: [],
            [ModerationRejectKey.html5ShareBuilding]: [],
            [ModerationRejectKey.html5BadText]: [],
            [ModerationRejectKey.html5Medicine]: []
        };

        if (
            reasons.length > 1 ||
            reasons.length === 0 ||
            (reasons.length === 1 &&
                MODERATION_REASONS_KEYS.includes(reasons[0].key))
        ) {
            return this.getFooterButtons(defaultLinks);
        }

        const links = reduce(
            reasons,
            (res: Array<FooterLink>, reason: ModerationRejectReason) => {
                const reasonLinks = moderationRejectReasonData[reason.key];

                reasonLinks.forEach((item: FooterLink) => {
                    if (!res.includes(item)) {
                        res.push(item);
                    }
                });

                return res;
            },
            []
        );

        return this.getFooterButtons(links.length !== 0 ? links : defaultLinks);
    };

    private getFooterButtons = (
        links: Array<FooterLink>
    ): Array<JSX.Element> => {
        const { isOperatorSelfAgency, lang, tld, contactEmail } = this.props;
        const dissentUrl = contactEmail
            ? `mailto:${contactEmail}`
            : UrlHelpers.getCommonHelpUrl(
                  'direct/troubleshooting/moderation.html#dissent',
                  tld
              );
        const sendDocsUrl = contactEmail
            ? `mailto:${contactEmail}`
            : UrlHelpers.getCommonHelpUrl(
                  'direct/troubleshooting/moderation.html#document',
                  tld
              );

        const linksDictionary = {
            [FooterLink.reasonsQuestion]: {
                text: i18n('ostalis-voprosy-o-prichinah'),
                url: isOperatorSelfAgency
                    ? UrlHelpers.getCommonHelpUrl(
                          'direct/troubleshooting/moderation.html',
                          tld
                      )
                    : dissentUrl
            },
            [FooterLink.generalAdvRules]: {
                text: i18n('polnyy-spisok-trebovaniy'),
                url: UrlHelpers.getLegalUrl('general_adv_rules', tld, lang)
            },
            [FooterLink.sendDocs]: {
                text: i18n('otpravit-dokumenty'),
                url: isOperatorSelfAgency
                    ? UrlHelpers.getCommonHelpUrl(
                          'direct/troubleshooting/moderation.html',
                          tld
                      )
                    : sendDocsUrl
            },
            [FooterLink.paymentReturn]: {
                text: i18n('ostavit-zayavku-na-vozvrat'),
                url: UrlHelpers.getCommonHelpUrl(
                    'direct/troubleshooting/payment.html#return',
                    tld
                )
            },
            [FooterLink.serviceQuestion]: {
                text: i18n('ostalis-drugie-voprosy-po'),
                url: dissentUrl
            },
            [FooterLink.checkAgain]: {
                text: i18n('oshibki-ustraneny-nuzhna-povtornaya-100'),
                url: dissentUrl
            },
            [FooterLink.falseInfoProofsText]: {
                text: i18n('esli-u-vas-est-param-link'),
                url: dissentUrl
            }
        };

        return links.map((link, index) => {
            const linkData = linksDictionary[link];

            return (
                <ButtonDecorator
                    key={index}
                    cls={Bem.cls(BLOCK, 'footer-btn')}
                    text={linkData.text}
                    theme={'pseudo'}
                    size={'n'}
                    type={'link'}
                    target={LinkTarget.blank}
                    url={linkData.url}
                />
            );
        });
    };
}
